// client/src/lib/api.js
// Thin fetch wrapper used by all TanStack Query hooks. credentials:
// 'same-origin' so the tictac_session cookie tags along on every request.
// Errors arrive as ApiError with `.status`, `.code`, `.details`, `.message`
// so callers can switch on code instead of parsing strings.

export class ApiError extends Error {
  constructor({ status, code, message, details }) {
    super(message || code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(method, path, { body, query } = {}) {
  let url = path;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v == null || v === '') continue;
      qs.set(k, String(v));
    }
    const qstr = qs.toString();
    if (qstr) url += (url.includes('?') ? '&' : '?') + qstr;
  }

  const init = {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);

  // 204 → no body
  if (res.status === 204) return null;

  const text = await res.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Server returned HTML (e.g. 404 page) or empty — leave payload null.
    }
  }

  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: payload?.error,
      message: payload?.message,
      details: payload?.details,
    });
  }
  return payload;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  delete: (path, opts) => request('DELETE', path, opts),
};

/**
 * Multipart upload of a single XLSX (field `file`). Same error shape as
 * `request` so callers can humanize `.status` / `.code` uniformly.
 */
export async function uploadXlsx(url, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(url, { method: 'POST', body: fd, credentials: 'same-origin' });
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: payload?.error,
      message: payload?.message || payload?.error,
      details: payload?.details,
    });
  }
  return payload;
}
